import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import { uniqBy } from 'lodash';
import styled from 'styled-components';
import {
  Query,
  QueryContactsArgs,
  QueryUsersArgs,
  ScheduleAttendeeCandidatesUnion,
  UserAvatar,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useAttendeeCandidates } from 'src/components/domain/attendees/hooks/use-attendee-candidates';
import { ContactStageIcon } from 'src/components/domain/contacts/contact-stage-icon';
import { GET_CONTACTS } from 'src/components/domain/contacts/hooks/use-contacts';
import { groupByRoleId } from 'src/components/domain/users/hooks/helpers';
import { COMPANY_USERS } from 'src/components/domain/users/hooks/use-company-users';
import { ImageChip, TextChip } from 'src/components/general/display/chip';
import { Select, SelectOption } from 'src/components/general/inputs/select/select';
import { InlineFormSelect } from 'src/components/general/modals/inline-form/inline-form-select';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { getNodesFromConnection } from 'src/utils/graphql/get-nodes-from-connection';

const OptionContactStageIcon = styled(ContactStageIcon)`
  height: 20px;
  width: 20px;
  margin: 0;

  .contact-stage-icon_icon svg {
    width: 10px;
    height: 10px;
  }
`;

const getAvatarChip = (avatar: UserAvatar): TextChip | ImageChip => {
  if (avatar.url) {
    return {
      url: avatar.url,
    };
  }

  return {
    text: avatar.initials,
    color: avatar.color,
  };
};

export type AttendeeCandidate =
  | { type: 'USER'; id: number }
  | { type: 'CONTACT'; id: number }
  | { type: 'EMAIL'; email: string };

type AttendeeSelectOption = SelectOption<string> & {
  attendee: AttendeeCandidate;
};

const encodeAttendeeValue = (
  type: AttendeeCandidate['type'],
  idOrEmail: number | string,
) => `${type}:${idOrEmail}`;

const getAttendeeCandidateKey = (attendee: AttendeeCandidate): number | string =>
  attendee.type === 'EMAIL' ? attendee.email : attendee.id;

export const encodeAttendeeCandidateId = (attendee: AttendeeCandidate): string =>
  `${attendee.type}-${getAttendeeCandidateKey(attendee)}`;

const emailAttendeeToSelectOption = (email: string): AttendeeSelectOption => ({
  value: encodeAttendeeValue('EMAIL', email),
  label: email,
  attendee: { type: 'EMAIL', email },
});

const candidateToSelectOptionMapper = (
  node: ScheduleAttendeeCandidatesUnion,
): AttendeeSelectOption => {
  if (node.__typename === 'Contact') {
    return {
      value: encodeAttendeeValue('CONTACT', node.id),
      label: node.label ?? '',
      attendee: { type: 'CONTACT', id: node.id },
      icon: (
        <OptionContactStageIcon slug={node.stage?.slug} name={node.stage?.name ?? ''} />
      ),
    };
  }

  return {
    value: encodeAttendeeValue('USER', node.id),
    label: node.fullName,
    attendee: { type: 'USER', id: node.id },
    chip: node.avatar ? getAvatarChip(node.avatar) : undefined,
  };
};

const StyledSelect = styled(InlineFormSelect)`
  .lgg-select__value-container--is-multi {
    gap: 5px;
    padding: 5px;

    .lgg-select__multi-value {
      height: 22px;
      max-height: 22px;
      padding: 3px 5px;
      margin: 0;

      .lgg-select__multi-value__remove {
        padding: 0px 0px 0px 5px;
      }
      .contact-stage-icon_container {
        width: 16px;
        height: 16px;
        margin-right: 5px;

        .contact-stage-icon_icon svg {
          width: 8px;
          height: 8px;
        }
      }
    }
  }
` as typeof Select;

type AttendeeCandidatesSelectProps = {
  selectedAttendees: AttendeeCandidate[];
  onChange: (value: AttendeeCandidate[]) => void;
  name: string;
  mobileLabel?: string;
  hideIndicators?: boolean;
  isClearable?: boolean;
  hasError?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  multiValueMaxToShow?: number;
  // Attendees that are always selected and can't be removed.
  fixedAttendees?: AttendeeCandidate[];
};

const isSameAttendee = (a: AttendeeCandidate, b: AttendeeCandidate) =>
  encodeAttendeeCandidateId(a) === encodeAttendeeCandidateId(b);

export const AttendeeCandidatesSelect = (props: AttendeeCandidatesSelectProps) => {
  const {
    selectedAttendees,
    onChange,
    isLoading,
    isDisabled,
    isClearable = true,
    hasError = false,
    hideIndicators = false,
    mobileLabel,
    name,
    multiValueMaxToShow = 5,
    fixedAttendees = [],
  } = props;

  const { t } = useTranslation(['common', 'appointments']);
  const currentInstitution = useCurrentInstitution();

  const [searchQuery, setSearchQuery] = useState('');
  const {
    candidates,
    loading: loadingCandidates,
    refetch,
    loadMore,
    hasNextPage,
  } = useAttendeeCandidates(searchQuery ? { search: searchQuery } : undefined);

  const options = useMemo(
    () => candidates.map(candidateToSelectOptionMapper),
    [candidates],
  );

  const groupedOptions = useMemo(() => {
    const userCandidates = candidates.filter(
      (
        candidate,
      ): candidate is Extract<ScheduleAttendeeCandidatesUnion, { __typename: 'User' }> =>
        candidate.__typename === 'User',
    );
    const contactCandidates = candidates.filter(
      (
        candidate,
      ): candidate is Extract<
        ScheduleAttendeeCandidatesUnion,
        { __typename: 'Contact' }
      > => candidate.__typename === 'Contact',
    );

    const userGroups = groupByRoleId(userCandidates, t).map(({ label, items }) => ({
      label,
      options: items.map(candidateToSelectOptionMapper),
    }));
    const contactOptions = contactCandidates.map(candidateToSelectOptionMapper);

    return contactOptions.length
      ? [...userGroups, { label: t('common:contacts'), options: contactOptions }]
      : userGroups;
  }, [candidates, t]);

  const [cachedOptions, setCachedOptions] = useState<AttendeeSelectOption[]>([]);

  useEffect(() => {
    if (options.length) {
      setCachedOptions((prevState) => uniqBy([...prevState, ...options], 'value'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingCandidates]);

  const [fetchExtraContacts] = useLazyQuery<
    Pick<Query, 'contacts'>,
    Partial<QueryContactsArgs>
  >(GET_CONTACTS, {
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
  });
  const [fetchExtraUsers] = useLazyQuery<Pick<Query, 'users'>, Partial<QueryUsersArgs>>(
    COMPANY_USERS,
    {
      fetchPolicy: 'no-cache',
      nextFetchPolicy: 'no-cache',
    },
  );

  // Tracks composite keys we've already tried to fetch (found or not), so a selected
  // attendee that doesn't resolve (e.g. deleted) doesn't get re-fetched on every render.
  const attemptedKeysRef = useRef<Set<string>>(new Set());

  // The merged candidates endpoint only supports free-text search, not id lookups, so
  // resolving a selected-but-not-yet-cached attendee falls back to the same per-domain
  // endpoints (and id-based where filters) the standalone contact/user selects use.
  useEffect(() => {
    const missing = selectedAttendees.filter((attendee) => {
      const key = encodeAttendeeValue(attendee.type, getAttendeeCandidateKey(attendee));

      return (
        !cachedOptions.some((option) => option.value === key) &&
        !attemptedKeysRef.current.has(key)
      );
    });

    if (!missing.length) {
      return;
    }

    missing.forEach((attendee) =>
      attemptedKeysRef.current.add(
        encodeAttendeeValue(attendee.type, getAttendeeCandidateKey(attendee)),
      ),
    );

    const missingEmailAttendees = missing.filter(
      (attendee): attendee is Extract<AttendeeCandidate, { type: 'EMAIL' }> =>
        attendee.type === 'EMAIL',
    );
    const missingContactIds = missing
      .filter((attendee) => attendee.type === 'CONTACT')
      .map((attendee) => attendee.id);
    const missingUserIds = missing
      .filter((attendee) => attendee.type === 'USER')
      .map((attendee) => attendee.id);

    void (async () => {
      const newOptions: AttendeeSelectOption[] = missingEmailAttendees.map((attendee) =>
        emailAttendeeToSelectOption(attendee.email),
      );

      if (missingContactIds.length) {
        const { data } = await fetchExtraContacts({
          variables: {
            institutionId: currentInstitution.id,
            where: { id: { _in: missingContactIds } },
          },
        });

        getNodesFromConnection(data?.contacts).forEach((contact) => {
          newOptions.push(candidateToSelectOptionMapper(contact));
        });
      }

      if (missingUserIds.length) {
        const { data } = await fetchExtraUsers({
          variables: {
            institutionId: currentInstitution.id,
            where: { id: { _in: missingUserIds }, isActive: { _eq: true } },
          },
        });

        getNodesFromConnection(data?.users).forEach((user) => {
          newOptions.push(candidateToSelectOptionMapper(user));
        });
      }

      if (newOptions.length) {
        setCachedOptions((prevState) => uniqBy([...prevState, ...newOptions], 'value'));
      }
    })();
  }, [
    cachedOptions,
    currentInstitution.id,
    fetchExtraContacts,
    fetchExtraUsers,
    selectedAttendees,
  ]);

  const value = useMemo(
    () =>
      selectedAttendees
        .map((attendee) => {
          const option = cachedOptions.find(
            (option) =>
              option.value ===
              encodeAttendeeValue(attendee.type, getAttendeeCandidateKey(attendee)),
          );

          return option
            ? {
                ...option,
                isFixed: fixedAttendees.some((fixed) => isSameAttendee(fixed, attendee)),
              }
            : undefined;
        })
        .filter(Boolean) as AttendeeSelectOption[],
    [cachedOptions, fixedAttendees, selectedAttendees],
  );

  return (
    <StyledSelect
      name={name}
      isSearchable
      isMulti
      onChange={(newValue) => {
        const options = newValue as (AttendeeSelectOption | SelectOption<string>)[];
        const attendees: AttendeeCandidate[] = options.map((option) =>
          'attendee' in option ? option.attendee : { type: 'EMAIL', email: option.value },
        );
        // Fixed attendees can still be removed with backspace, by clearing the select or
        // by deselecting them in the menu, so they're added back here.
        const removedFixedAttendees = fixedAttendees.filter(
          (fixed) => !attendees.some((attendee) => isSameAttendee(fixed, attendee)),
        );

        onChange([...removedFixedAttendees, ...attendees]);
      }}
      onMenuClose={async () => {
        setSearchQuery('');
        await refetch({ where: {} });
      }}
      onSearch={async (query) => {
        setSearchQuery(query);
      }}
      mobileLabel={mobileLabel}
      isClearable={isClearable}
      canLoadMore={hasNextPage}
      onLoadMore={async () => {
        await loadMore();
      }}
      isLoading={loadingCandidates || isLoading}
      isDisabled={isDisabled}
      hasError={hasError}
      hideIndicators={hideIndicators}
      value={value}
      options={groupedOptions}
      disableInternalSearch
      placeholder={t('appointments:appointmentInlineForm.fields.attendees.placeholder')}
      isCreatable
      multiValueMaxToShow={multiValueMaxToShow}
    />
  );
};
